import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { UserCog, BellRing, Database, MessageSquare, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../contexts/SettingsContext';

const VALID_SETTINGS_TABS = new Set(['user', 'notifications', 'chat', 'data', 'general']);

const SettingsSidebar = () => {
  const { t } = useTranslation('settings');
  const { activeTab, setActiveTab } = useSettings();

  const MENU_ITEMS = useMemo(
    () => [
      {
        id: 'user',
        label: t('sidebar.menu.user.label'),
        description: t('sidebar.menu.user.description'),
        icon: UserCog,
      },
      {
        id: 'notifications',
        label: t('sidebar.menu.notifications.label'),
        description: t('sidebar.menu.notifications.description'),
        icon: BellRing,
      },
      {
        id: 'chat',
        label: t('sidebar.menu.chat.label'),
        description: t('sidebar.menu.chat.description'),
        icon: MessageSquare,
      },
      {
        id: 'data',
        label: t('sidebar.menu.data.label'),
        description: t('sidebar.menu.data.description'),
        icon: Database,
      },
      {
        id: 'general',
        label: t('sidebar.menu.general.label'),
        description: t('sidebar.menu.general.description'),
        icon: FileText,
      },
    ],
    [t]
  );

  const safeTab = VALID_SETTINGS_TABS.has(activeTab) ? activeTab : 'user';

  return (
    <div className="w-80 flex flex-col overflow-hidden transition-colors duration-300 border-r bg-white dark:bg-[var(--color-bg-base)] dark:border-slate-800 border-gray-200 dark:border-slate-800">
      <div className="p-6 border-b backdrop-blur-lg border-gray-200/60 dark:border-slate-800">
        <h2 className="text-xl font-bold text-gray-900 dark:text-[#e8f0fb]">
          {t('sidebar.title')}
        </h2>
        <p className="text-sm mt-1 text-gray-500 dark:text-[var(--color-text-secondary)]">
          {t('sidebar.subtitle')}
        </p>
      </div>

      <nav className="flex-1 overflow-y-auto py-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
        {MENU_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = safeTab === item.id;

          return (
            <motion.button
              key={item.id}
              type="button"
              onClick={() => setActiveTab(item.id)}
              whileHover={{ x: 4 }}
              whileTap={{ scale: 0.98 }}
              className={`w-full text-left px-5 py-4 transition-all duration-200 border-l-4 ${
                isActive
                  ? 'bg-blue-50 dark:bg-[var(--color-bg-elevated)] text-blue-700 dark:text-[#e8f0fb] border-blue-600 dark:border-blue-500'
                  : 'hover:bg-gray-100/90 dark:hover:bg-[var(--color-bg-base)] text-gray-600 dark:text-[var(--color-text-secondary)] border-transparent'
              }`}
            >
              <div className="flex items-center space-x-4">
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                    isActive
                      ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                      : 'bg-gray-100 dark:bg-[var(--color-bg-elevated)] text-gray-500 dark:text-[var(--color-text-secondary)] border border-transparent'
                  }`}
                >
                  <Icon className="w-5 h-5" strokeWidth={isActive ? 2.6 : 2.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className={`font-semibold text-sm ${
                      isActive ? 'text-blue-700 dark:text-[#e8f0fb]' : 'text-gray-800 dark:text-[#e8f0fb]'
                    }`}
                  >
                    {item.label}
                  </p>
                  <p
                    className={`text-xs ${
                      isActive ? 'text-blue-300/80' : 'text-gray-500 dark:text-[var(--color-text-secondary)]'
                    }`}
                  >
                    {item.description}
                  </p>
                </div>
                {isActive && (
                  <motion.span
                    layoutId="settings-active-pill"
                    className="w-2 h-10 rounded-full bg-blue-500/80 dark:bg-blue-500/80 shadow-[0_0_12px_rgba(59,130,246,0.45)]"
                  />
                )}
              </div>
            </motion.button>
          );
        })}
      </nav>
    </div>
  );
};

export default SettingsSidebar;
